import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../base-query';
import { serializeQueryArgs } from '../serialize-query-args';

export const BUSINESS_DOCUMENTS_TAGS = Object.freeze({
  INDEX: 'business_documents_index',
  SLOTS: 'business_document_slots',
});

// Every write changes what the slots hold, so it refreshes them as well as the list.
const invalidatesOnSuccess = (_, error) => (error ? [] : Object.values(BUSINESS_DOCUMENTS_TAGS));

// A slot upload is stored before it goes to Stripe, so a failed response can still have changed
// the slots: refresh them either way.
const invalidatesAlways = () => Object.values(BUSINESS_DOCUMENTS_TAGS);

export const businessDocumentsApiSlice = createApi({
  tagTypes: Object.values(BUSINESS_DOCUMENTS_TAGS),
  reducerPath: 'businessDocumentsApiSlice',
  baseQuery,
  endpoints: (builder) => ({
    getBusinessDocuments: builder.query({
      query: ({ apiManager, params }) => apiManager.getDocuments(params),
      providesTags: [BUSINESS_DOCUMENTS_TAGS.INDEX],
    }),
    // Callers pass the company and its legal form too, for the cache key only: the company goes
    // in a header, and a new legal form changes which slots are required.
    getDocumentSlots: builder.query({
      query: ({ apiManager }) => apiManager.getDocumentSlots(),
      providesTags: [BUSINESS_DOCUMENTS_TAGS.SLOTS],
    }),
    submitBusinessDocument: builder.mutation({
      query: ({ apiManager, data }) => apiManager.submit(data),
      invalidatesTags: invalidatesOnSuccess,
    }),
    uploadBusinessDocument: builder.mutation({
      query: ({ apiManager, data }) => apiManager.uploadDocument(data),
      invalidatesTags: invalidatesOnSuccess,
    }),
    uploadSlotDocument: builder.mutation({
      query: ({ apiManager, slot, data }) => apiManager.uploadSlotDocument(slot, data),
      invalidatesTags: invalidatesAlways,
    }),
    replaceBusinessDocument: builder.mutation({
      query: ({ apiManager, id, data }) => apiManager.replaceDocument(id, data),
      invalidatesTags: invalidatesAlways,
    }),
    removeBusinessDocument: builder.mutation({
      query: ({ apiManager, id }) => apiManager.removeDocument(id),
      invalidatesTags: invalidatesAlways,
    }),
    deleteBusinessDocument: builder.mutation({
      query: ({ apiManager, ids, isHardDelete }) => apiManager.deleteDocuments({ ids, isHardDelete }),
      invalidatesTags: invalidatesOnSuccess,
    }),
  }),
  serializeQueryArgs,
});

export const {
  useGetBusinessDocumentsQuery,
  useGetDocumentSlotsQuery,
  useSubmitBusinessDocumentMutation,
  useUploadBusinessDocumentMutation,
  useUploadSlotDocumentMutation,
  useReplaceBusinessDocumentMutation,
  useRemoveBusinessDocumentMutation,
  useDeleteBusinessDocumentMutation,
} = businessDocumentsApiSlice;
